import { useState, useEffect, useRef } from "react";
import { Zap, Flame, ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";
import { SHOW_EOFY_BANNER, EOFY_DISCOUNT_LABEL } from "enums/index";
import PlanDetails from "sections/modal/plan-details";
import CloseButton from "components/ui/close-button";

const PROMPT_HEADERS = [
  "That's a solid run of notes.",
  "The paperwork never stood a chance.",
  "Another one written up.",
  "That's today's charting handled.",
  "Session after session, quietly handled.",
];

const FEATURES = [
  "Unlimited patient sessions",
  "Unlimited notes generation",
  "Priority transcription",
  "Advanced templates & customisation",
];

const UpgradePromptModal = ({
  onClose,
  stripeProducts,
  usageData,
  subscriptionData,
  loading,
  handleClickGetStarted,
}) => {
  const [showPlans, setShowPlans] = useState(false);
  const [headerIdx] = useState(() => Math.floor(Math.random() * PROMPT_HEADERS.length));
  const overlayRef = useRef(null);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      if (showPlans) setShowPlans(false);
      else onClose();
    }
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") {
        if (showPlans) setShowPlans(false);
        else onClose();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose, showPlans]);

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[999] flex items-center justify-center p-4"
      style={{
        background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(4px)",
      }}
    >
      {showPlans ? (
        /* ── Plans view ── */
        <div
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-[900px] bg-white rounded-ns-tile shadow-pop overflow-y-auto"
          style={{ maxHeight: "85vh" }}
        >
          <CloseButton
            onClick={() => setShowPlans(false)}
            className="absolute right-4 top-3 z-10"
          />
          <div className="py-6 px-6 w-full">
            <PlanDetails
              loading={loading}
              products={stripeProducts}
              userUsageData={usageData}
              subscriptionData={subscriptionData}
              handleClickGetStarted={handleClickGetStarted}
            />
          </div>
        </div>
      ) : (
        /* ── Promo view ── */
        <div
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-sm bg-white rounded-ns-tile shadow-pop overflow-hidden"
          style={{ animation: "modal-in 0.35s cubic-bezier(0.16,1,0.3,1)" }}
        >
          {SHOW_EOFY_BANNER && (
            <div
              className="flex items-center justify-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider"
              style={{
                background: "var(--ns-gradient-cta)",
                backgroundSize: "200% 100%",
                animation: "shimmer 2.5s linear infinite",
                color: "white",
              }}
            >
              <Flame className="h-3.5 w-3.5 flex-shrink-0" />
              {EOFY_DISCOUNT_LABEL} — Limited time offer
              <Flame className="h-3.5 w-3.5 flex-shrink-0" />
            </div>
          )}

          <CloseButton onClick={onClose} className="absolute right-3 top-3 z-10" />

          <div
            className="px-6 pt-6 pb-5 text-center"
            style={{ background: "var(--ns-shade-50)" }}
          >
            <div
              className="inline-flex items-center justify-center w-14 h-14 rounded-ns-tile mb-4"
              style={{
                background: "var(--ns-accent)",
                animation: "pulse-icon 2s ease-in-out infinite",
                boxShadow: "0 8px 24px rgba(99,102,241,0.35)",
              }}
            >
              <Sparkles className="h-7 w-7 text-white" />
            </div>
            <h2
              className="text-lg font-bold text-ink mb-1"
              style={{ animation: "slide-up-fade 0.4s ease-out 0.1s both" }}
            >
              {PROMPT_HEADERS[headerIdx]}
            </h2>
            <p
              className="text-sm text-muted"
              style={{ animation: "slide-up-fade 0.4s ease-out 0.2s both" }}
            >
              Pro removes the limits. Same app, no ceiling.
            </p>
          </div>

          <div className="px-6 py-4 space-y-2.5">
            {FEATURES.map((feature, i) => (
              <div
                key={i}
                className="flex items-center gap-2.5"
                style={{ animation: `slide-up-fade 0.4s ease-out ${0.25 + i * 0.07}s both` }}
              >
                <CheckCircle2 className="h-4 w-4 text-accent flex-shrink-0" />
                <span className="text-sm text-muted">{feature}</span>
              </div>
            ))}
          </div>

          <div
            className="px-6 pb-6 pt-2 flex flex-col gap-2.5"
            style={{ animation: "slide-up-fade 0.4s ease-out 0.55s both" }}
          >
            <button
              onClick={(e) => { e.stopPropagation(); setShowPlans(true); }}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-ns text-sm font-semibold text-white transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: "var(--ns-accent)",
                boxShadow: "0 4px 14px rgba(99,102,241,0.4)",
              }}
            >
              <Zap className="h-4 w-4" />
              Upgrade to Pro — 14 Day Free Trial
              <ArrowRight className="h-4 w-4" />
            </button>

            <button
              onClick={onClose}
              className="w-full py-2.5 px-4 rounded-ns text-sm font-medium text-muted hover:text-muted hover:bg-shade-50 transition-all duration-200"
            >
              Stay on free trial
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes modal-in {
          from { opacity: 0; transform: scale(0.92) translateY(16px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes slide-up-fade {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse-icon {
          0%, 100% { box-shadow: 0 8px 24px rgba(99,102,241,0.35); transform: scale(1); }
          50% { box-shadow: 0 8px 32px rgba(99,102,241,0.55); transform: scale(1.06); }
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
};

export default UpgradePromptModal;
